import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { SystemSuggestedJob } from 'src/app/shared/models/system-suggested-job';
import { appendQuery, combine, getQueryString } from 'src/app/shared/utilities/route';
import { FeatureFlag } from '../models/enums/feature-flag';
import { JobAlertDto } from '../models/job-alert.model';
import { JobGridFacility } from '../models/job-grid-facility.model';
import { JobGridItem } from '../models/job-grid-item.model';
import { JobGridLicensure } from '../models/job-grid-licensure.model';
import { JobGridMapLocation } from '../models/job-grid-map-location.model';
import { JobGridResponseContainer } from '../models/job-grid-response-container.model';
import { JobSubmittalGridItem } from '../models/job-grid-submittal-item.dto';
import { JobGridWeeklyPayRanges } from '../models/job-grid-weekly-pay-ranges.model';
import { JobGridLookups } from './../models/job-grid-lookups';
import { JobGridResponse } from './../models/job-grid-response';
import { JobLicensureFiles } from './../models/job-licensure-files.model';
import { LDFeatureManager as LdFeatureManager } from './ld-feature-manager';
import { JobChangeSet } from 'src/app/accountmanagers/details/job-history/models/job-change-set.model';
import { JobChange } from 'src/app/accountmanagers/details/job-history/models/job-change.model';
import { JobRefreshHistory } from 'src/app/accountmanagers/details/job-history/models/job-refresh-history.model';

@Injectable({
    providedIn: 'root'
})
export class JobsService {
    private coreApiPath: string;
    // Define fields for 'desc' sorting by first clicking on a header grid;
    private readonly descJobsGridFieldsFilter = [
        'blendedBillRate',
        'regularPayHigh',
        'openPositions',
        'programDisplayName',
        'updatedDate',
        'startDate',
        'postedDate',
        'duration'
    ];

    constructor(
        @Inject(APP_CONFIG) private readonly settings: Settings,
        private readonly http: HttpClient,
        private readonly _ldFeatureManager: LdFeatureManager
    ) {
        this.coreApiPath = combine(settings.coreAPIUrl, 'ayanova');
    }

    //There shouldn't be ANY shared grids. Please use domain separation!
    searchJobs(args: any, overrideSortDir = true): Observable<JobGridResponse> {
        let url = `${this.coreApiPath}/jobs`;
        if (args.sort && args.sort.field && args.sort.field.length) {
            if (this.descJobsGridFieldsFilter.includes(args.sort.field) && overrideSortDir) {
                // Swapping direction filter props for defined fields;
                url = appendQuery(
                    url,
                    `sortField=${args.sort.field}&sortType=${args.sort.dir === 'asc' ? 'desc' : 'asc'}`
                );
            } else {
                // Default direction;
                url = appendQuery(url, `sortField=${args.sort.field}&sortType=${args.sort.dir || 'asc'}`);
            }
        }

        return this.getJobGridPaged(args.pageSize, args.skip, url, args.filter);
    }

    getGridLookups(): Observable<JobGridLookups> {
        const url = `${this.coreApiPath}/Jobs/Lookups`;
        return this.http.get<JobGridLookups>(url);
    }

    getJobDetails(jobId: number): Observable<JobGridItem> {
        const url = `${this.coreApiPath}/jobs/${jobId}`;
        return this.http.get<JobGridItem>(url);
    }

    getFacilities(facilityId: number): Observable<JobGridFacility[]> {
        const url = `${this.coreApiPath}/jobs/facilities/${facilityId}`;
        return this.http.get<JobGridFacility[]>(url);
    }

    getLicensures(stateId: number): Observable<JobGridLicensure[]> {
        const url = `${this.coreApiPath}/jobs/licensures/${stateId}`;
        return this.http.get<JobGridLicensure[]>(url);
    }

    getWeeklyPayRates(jobId: number): Observable<JobGridWeeklyPayRanges> {
        const url = `${this.coreApiPath}/jobs/${jobId}/weekly-pay-ranges`;
        return this.http.get<JobGridWeeklyPayRanges>(url);
    }

    getLicensureFiles(stateId: number): Observable<JobLicensureFiles[]> {
        const url = `${this.coreApiPath}/jobs/licensure-files/${stateId}`;
        return this.http.get<JobLicensureFiles[]>(url);
    }

    getJobsLocation(filter: any): Observable<JobGridMapLocation[]> {
        let url = `${this.coreApiPath}/jobs/locations`;
        if (filter) {
            url += getQueryString(filter);
        }
        // only return job locations if latitude/longitude exist
        return this.http.get<JobGridMapLocation[]>(url, { observe: 'response' }).pipe(
            map((resp) => {
                const jobLocations = resp.body;
                return jobLocations?.filter((elem) => elem.latitude !== 0 && elem.longitude !== 0) ?? [];
            })
        );
    }

    getJobSubmittals(id: number): Observable<JobSubmittalGridItem[]> {
        return this.http.get<JobSubmittalGridItem[]>(`${this.coreApiPath}/jobs/${id}/submittals`);
    }

    getJobChangeHistory(jobId: number): Observable<JobChangeSet[]> {
        return this.http.get<JobChangeSet[]>(`${this.coreApiPath}/job/${jobId}/history`);
    }

    getJobChangeDetails(jobId: number, transactionId: number): Observable<JobChange[]> {
        return this.http.get<JobChange[]>(`${this.coreApiPath}/job/${jobId}/history/${transactionId}`);
    }

    getJobRefreshHistory(jobId: number): Observable<JobRefreshHistory> {
        return this.http.get<JobRefreshHistory>(`${this.coreApiPath}/job/${jobId}/refreshHistory`);
    }

    getJobSuggestions(userIds: number[]): Observable<SystemSuggestedJob[]> {
        return this._ldFeatureManager.isEnabled(FeatureFlag.TurnOffNovaJobSuggestions).pipe(
            mergeMap((isEnabled: boolean) => {
                if (isEnabled) {
                    return of([]);
                }

                return this.http.get<SystemSuggestedJob[]>(
                    `${this.coreApiPath}/job-suggestions/suggestions/recruiters`,
                    { params: { userIds } }
                );
            })
        );
    }

    protected getJobGridPaged(pageSize: number, skip: number, url: string, params: any): Observable<JobGridResponse> {
        const headers = { 'content-type': 'application/json' };
        const body = params;
        const path = appendQuery(url, `pageSize=${pageSize}`, `skip=${skip}`);
        // using post, because querystring can be too long for filters
        return this.http.post<JobGridResponseContainer>(path, body, { headers: headers }).pipe(
            map((resp) => {
                const totalCount = resp.results.total;
                const totalOpenPositionsCount = resp.totalOpenPositions;
                const totalFilteredOpenPositionsCount = resp.totalFilteredOpenPositions;
                return {
                    total: totalCount,
                    data: resp.results.data,
                    totalOpenPositions: totalOpenPositionsCount,
                    totalFilteredOpenPositions: totalFilteredOpenPositionsCount
                };
            })
        );
    }

    getJobAlerts(): Observable<JobAlertDto[]> {
        const url = `${this.coreApiPath}/jobs/alerts`;

        return this.http.get<JobAlertDto[]>(url);
    }
}
